import type { AxiosError, AxiosResponse } from 'axios';
import { useState } from 'react';
import ReactTextareaAutosize from 'react-textarea-autosize';

import SpaceY from '../../components/SpaceY';
import UImage from '../../components/UImage';
import AlertModal from '../../Member/components/AlertModalV2';
import { handleError } from '../common';

import { useAlertModal } from './hook';
import useMeQuery from './react-query/user/useMeQuery';
import { useHistoryMethod } from './useHistoryMethod';
import { usePathnameMethod } from './usePathnameMethod';

const REPORT_REASONS = [
  '도배 및 중복',
  '주제와 맞지 않음',
  '저작권 침해 우려',
  '비방욕설이 심함',
  '기타',
] as const;
type ReportReasonType = typeof REPORT_REASONS;
type ReportReasonKeys = ReportReasonType[number];

const checkedIcon = `https://uglyus.s3.ap-northeast-2.amazonaws.com/assets/svg/checked-circle-brand_5LgfaEQWK.svg`;
const uncheckedIcon = `https://uglyus.s3.ap-northeast-2.amazonaws.com/assets/svg/unchecked-circle-gray200_nbKVMNF8g.svg`;

export interface ReportDto {
  reason: string;
  reasonDetail: string;
}

interface UseReportingModalProps {
  handleBlock: (reportDto: ReportDto) => Promise<AxiosResponse<void>>;
  onCompleteBlock: () => void;
  onClose?: () => void;
}

const useReportingModal = ({
  handleBlock,
  onCompleteBlock,
  onClose,
}: UseReportingModalProps) => {
  const { me } = useMeQuery();
  const history = useHistoryMethod();
  const { pathname } = usePathnameMethod();
  const { openAlertModal } = useAlertModal();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [reasonDetail, setReasonDetail] = useState('');
  const [reason, setReason] = useState<ReportReasonKeys | null>(null);
  const [tryOnComplete, setTryOnComplete] = useState(false);

  const canSubmit = reason !== null;

  const showWarning = tryOnComplete && !canSubmit;

  const openReportingModal = () => {
    if (!me) return history.pushToLogin({ redirectURI: pathname });

    setIsOpen(true);
  };

  const closeReportingModal = () => {
    setIsOpen(false);
    setReasonDetail('');
    setReason(null);
    setTryOnComplete(false);
    onClose?.();
  };

  const onComplete = () => {
    setTryOnComplete(true);

    if (!isOpen || !canSubmit || !reason) return;

    const reportDto = { reason, reasonDetail };

    const handleReportError = (e: AxiosError) => {
      closeReportingModal();
      handleError(openAlertModal, e, '신고하기');
    };

    handleBlock(reportDto).then(onCompleteBlock).catch(handleReportError);
  };

  const completeButtonColor: 'bg-gray-300' | 'bg-soil-brown-700' = canSubmit
    ? 'bg-soil-brown-700'
    : 'bg-gray-300';

  const props = {
    open: isOpen,
    onClose: closeReportingModal,
    onCancel: closeReportingModal,
    onComplete,
    title: '신고 이유를 말씀해주세요!',
    completeButtonColor,
    preventOnCompleteClose: true,
    content: (
      <div data-testid="reporting-modal">
        {showWarning && (
          <h5 className="mb-12px text-16px leading-6 text-red-500">
            신고하신 이유 1가지를 선택해주세요.
          </h5>
        )}
        <SpaceY height={8} />
        <div className="flex flex-col gap-16px">
          {REPORT_REASONS.map((reportReason) => (
            <div
              key={reportReason}
              onClick={() => setReason(reportReason)}
              className="flex cursor-pointer items-center gap-8px"
            >
              <UImage
                src={reportReason === reason ? checkedIcon : uncheckedIcon}
                alt="checked"
                className="flex-shrink-0"
                width={24}
                height={24}
              />
              <div className="flex-grow text-16px font-normal">
                {reportReason}
              </div>
            </div>
          ))}
        </div>

        <SpaceY height={20} />
        <ReactTextareaAutosize
          placeholder="더 상세한 내용을 남겨주시면 꼭 참고해 서비스 개선에 반영하겠습니다."
          value={reasonDetail}
          onChange={(e) => setReasonDetail(e.target.value)}
          minRows={3}
          className={`font-body1 w-full rounded-sm border border-gray-200 font-normal placeholder-gray-400 transition focus:border-brand-1`}
          style={{ resize: 'none', padding: '14px 16px' }}
        />
      </div>
    ),
    hasCancelButton: true,
  };

  return {
    isOpen,
    openReportingModal,
    ReportingModalComponent: <AlertModal {...props} />,
  };
};

export default useReportingModal;
